import type { EquipmentType, VirtualDeviceConfiguration } from "@irriga-mais/common";
import type { AUTDevice } from "@/api/queries/account";
import type { VirtualDeviceConfigurationWithModel } from "../store/data";
import { findDeviceModelsCompatibleInputs, findDeviceModelsCompatibleOutputs } from "./device-model";

export function getSectorVirtualDeviceConfig<VDC extends VirtualDeviceConfiguration>(
  sectorId: string,
  virtualDeviceConfigurations: VDC[]
) {
  return virtualDeviceConfigurations.find((vdc) => vdc.sector === sectorId);
}

export function getProjectBackwashVirtualDeviceConfig<VDC extends VirtualDeviceConfiguration>(
  projectId: string,
  virtualDeviceConfigurations: VDC[]
) {
  return virtualDeviceConfigurations.find((vdc) => vdc.project_backwash === projectId);
}

export function getWaterPumpVirtualDeviceConfig<VDC extends VirtualDeviceConfiguration>(
  waterPumpId: string,
  virtualDeviceConfigurations: VDC[]
) {
  return virtualDeviceConfigurations.find((vdc) => vdc.water_pump === waterPumpId);
}

export function getReservoirVirtualDeviceConfig<VDC extends VirtualDeviceConfiguration>(
  reservoirId: string,
  virtualDeviceConfigurations: VDC[]
) {
  return virtualDeviceConfigurations.find((vdc) => vdc.reservoir === reservoirId);
}

/**
 * Filter available outputs for a given set of devices, excluding those already in use by virtual devices.
 * The result is an array of objects, each containing a device and the available outputs for that device.
 * The available outputs are those that are compatible with the given equipment type, if any.
 * @param devices
 * @param virtualDeviceConfigurations
 * @param elementType
 * @returns
 */
export function filterAvailableOutputs(
  devices: AUTDevice[],
  virtualDeviceConfigurations: VirtualDeviceConfigurationWithModel[],
  elementType?: EquipmentType
): Array<{
  device: AUTDevice;
  /**
   * The indexes of the outputs that are available.
   * It is one based, so the first output has index 1.
   */
  outputs: number[];
}> {
  const result: Array<{
    device: AUTDevice;
    outputs: number[];
  }> = [];

  devices.forEach((device) => {
    const usedConfigurations = virtualDeviceConfigurations.filter((vdc) => vdc.device.id === device.id);
    const usedOutputs = new Set<number>();
    usedConfigurations.forEach((vdc) => {
      usedOutputs.add(vdc.turn_on_physical_output_index);
      if (vdc.turn_off_physical_output_index) {
        usedOutputs.add(vdc.turn_off_physical_output_index);
      }
    });
    const compatibleOutputs = elementType
      ? (findDeviceModelsCompatibleOutputs([device.model], elementType).at(0)?.outputs ?? [])
      : (device.model.outputs?.map((_, index) => index + 1) ?? []);
    result.push({
      device,
      outputs: compatibleOutputs.filter((output) => !usedOutputs.has(output)),
    });
  });

  return result;
}

export function filterDeviceAvailableInputs(
  device: AUTDevice,
  virtualDeviceConfigurations: VirtualDeviceConfigurationWithModel[],
  elementType?: EquipmentType
): number[] {
  const usedConfigurations = virtualDeviceConfigurations.filter((vdc) => vdc.device.id === device.id);
  const usedInputs = new Set<number>();
  usedConfigurations.forEach((vdc) => {
    if (vdc.monitoring_physical_input_index) {
      usedInputs.add(vdc.monitoring_physical_input_index);
    }
  });
  const compatibleInputs = elementType
    ? (findDeviceModelsCompatibleInputs([device.model], elementType).at(0)?.inputs ?? [])
    : (device.model.inputs?.map((_, index) => index + 1) ?? []);
  return compatibleInputs.filter((input) => !usedInputs.has(input));
}
